// Global background upload tray, mounted once in AppShell. Reads the
// resumable uploader's store, so uploads keep running (and show progress)
// across navigation. Finished uploads drop out, and so do those a chat message's tile shows;
// failures stay until dismissed.

import { useEffect } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  UPLOAD_STATUS_LABELS,
  useMediaUploadStore,
  type UploadItem,
} from '@/stores/useMediaUploadStore'

/** Uploads worth showing in the tray: everything not yet finished that no chat message's tile
 * already shows. */
function trayItems(
  uploads: Record<string, UploadItem>,
  onMessage: Record<string, true>
): UploadItem[] {
  return Object.values(uploads).filter(
    (item) => item.status !== 'done' && !onMessage[item.upload_id]
  )
}

export function UploadTray() {
  const uploads = useMediaUploadStore((state) => state.uploads)
  const onMessage = useMediaUploadStore((state) => state.onMessage)
  const resumePending = useMediaUploadStore((state) => state.resumePending)
  const retryUpload = useMediaUploadStore((state) => state.retryUpload)
  const removeUpload = useMediaUploadStore((state) => state.removeUpload)

  // Picks up uploads left in flight by a reload or a closed tab.
  useEffect(() => {
    resumePending()
  }, [resumePending])

  const items = trayItems(uploads, onMessage)
  if (items.length === 0) return null

  return (
    <div
      className="bg-background fixed right-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 w-[min(22rem,calc(100vw-1.5rem))] space-y-2 rounded-lg border p-3 shadow-lg md:bottom-4"
      role="status"
      aria-live="polite"
      data-testid="upload-tray"
    >
      {items.map((item) => (
        <div key={item.upload_id} className="flex items-center gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate text-sm font-medium">{item.filename}</p>
            <Progress
              value={item.progress}
              aria-label={`Upload progress for ${item.filename}`}
              className="h-1.5"
            />
            <p className="text-muted-foreground text-xs">
              {item.error ?? `${UPLOAD_STATUS_LABELS[item.status]} · ${item.progress}%`}
            </p>
          </div>
          {item.status === 'error' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => retryUpload(item.upload_id)}
              aria-label="Retry upload"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void removeUpload(item.upload_id)}
            aria-label="Dismiss upload"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  )
}
